import React, { useEffect, useState } from 'react';
import moment from 'moment-mini';
import { ActivityIndicator, FlatList, Keyboard } from 'react-native';
import { Icons } from '../../Components';
import { ProfileApi } from '../api';
import { button_styles } from '../../constants/styles';
import type { FocusPositionProps, MyPlayerProps } from '../../types';
import { Button, Text, TextInput, View } from '../../Components/Themed';
import { useColors } from '../../constants/useColors';

type DOBManagerProps = {
    player:MyPlayerProps,
    walkthrough?:boolean,
    onNext:() => void,
    onFocusPosition?:(data:FocusPositionProps) => void,
    onClose:() => void,
    onPlayerUpdate:(player:MyPlayerProps) => void
}
const sections = ['header','dob'];
const DOBManager = ({ player, walkthrough, onFocusPosition, onPlayerUpdate, onNext, onClose }: DOBManagerProps ) => {
    const C = useColors();
    const [ loading, setLoading ] = useState(false);
    const [ dob, setDob ] = useState<string>('');
    const [ error, setError ] = useState<undefined | string>(undefined)
    const [ age_error, setAgeError ] = useState<undefined | string>(undefined)

    useEffect(() => {
        if(player.dob){ 
            let split_dob = player.dob.split('T')
            if(!split_dob[0]){ return }
            split_dob = split_dob[0].split('-')
            let new_date = `${split_dob[1]}/${split_dob[2]}/${split_dob[0]}`
            let valid = moment(new_date, 'MM/DD/YYYY', true).isValid()
            if(valid){ setDob(new_date) }
            else { setDob(moment(player.dob).format('MM/DD/YYYY')) }
            let age_diff = moment().diff(moment(player.dob), 'years')
            if(age_diff < 18){
                setAgeError('Must be over the age of 18 to participate in real money activities on BettorEdge')
            } else {
                setAgeError(undefined)
            }
        }
    },[player])

    const isUpdateNeeded = () => {
        let new_dob = moment(dob, 'MM/DD/YYYY').format('MM/DD/YYYY');
        let old_dob = moment(player.dob, 'YYYY-MM-DD').format('MM/DD/YYYY');
        if(new_dob != old_dob){ return true }
        return false
    }

    const handleDOBChange = (val:string) => {
        let dob_moment = moment(val, 'MM/DD/YYYY', true)
        if(val != '' && !dob_moment.isValid()){ 
            setError('Date must be MM/DD/YYYY format') 
        } else { 
            setError(undefined) 
            let age_diff = moment().diff(dob_moment, 'years')
            if(age_diff < 18){
                setAgeError('Must be over the age of 18 to participate in real money activities on BettorEdge')
            } else {
                setAgeError(undefined)
            }
        }
        setDob(val)
    }

    const update_allowed = isUpdateNeeded() && !error
    const handleUpdatePlayerDOB = async() => {
        setLoading(true);
        const new_player = await ProfileApi.updatePlayerDOB(dob);
        onPlayerUpdate(new_player)
        setLoading(false);
    }

    const handleNext = async() => {
        if(!error && !isUpdateNeeded()){ return onNext() }
        if(!update_allowed){ return }
        await handleUpdatePlayerDOB()
        return onNext()
    }

    const renderSections = (data: { item:string, index:number }) => {
        switch(data.item){
            case 'header':
                return (
                    <View style={{ padding:20 }}>
                        <Text size={26} theme='h1'>Date Of Birth</Text>
                        <Text style={{ marginTop:15 }} size={14} theme='h2'>In order to participate in real-money gaming you must be over 18.</Text>
                    </View>
                )
            case 'dob':
                return (
                    <View style={{ padding:30, flex:1 }}>
                        <View type='input' style={{flexDirection:'row', alignItems:'center' }}>
                            <View transparent style={{ paddingLeft:15, paddingRight:15 }}>
                                <Icons.BirthdayIcon
                                    color={C.text.h1}
                                    size={18}
                                />
                            </View>
                            <TextInput
                                value={dob}
                                autoFocus
                                onFocusPosition={onFocusPosition}
                                keyboardType='default'
                                placeholder='01/01/2005'
                                editable={player.vouched_status == 'unverified' ? true : false}
                                style={{ flex:1 }}
                                onChangeText={(text) => handleDOBChange(text)}
                            />
                            <Button transparent style={{ padding:0, paddingLeft:15, paddingRight:15 }} onPress={() => Keyboard.dismiss()}>
                                {loading ?
                                <ActivityIndicator size='small' color={C.text.h1}/>
                                :
                                <View transparent>
                                    { player.dob && !error ?
                                    <Icons.CheckIcon size={16} color={C.text.success} />
                                    :
                                    <Icons.AlertIcon size={16} color={C.text.warning} />
                                    }
                                </View>
                                }
                            </Button>
                        </View>
                        {error ?
                        <Text size={14} color={C.text.warning}>{error}</Text>
                        : age_error ?
                        <Text size={14} color={C.text.warning}>{age_error}</Text>
                        :<></>}
                        {player.vouched_status !== 'unverified' && (
                            <View style={{ marginTop: 15, padding: 15, backgroundColor:'rgba(255, 193, 7, 0.1)', borderRadius: 8, borderWidth: 1, borderColor: C.absolutes.utility.warning }}>
                                <View transparent style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
                                    <Icons.AlertIcon size={20} color={C.text.warning}/>
                                    <View transparent style={{ flex: 1, marginLeft:10 }}>
                                        <Text theme='h2' size={14} style={{ marginBottom: 8 }}>DOB Update Restricted</Text>
                                        <Text theme='description' size={12} style={{ lineHeight: 18 }}>
                                            Your account verification status prevents DOB updates. Please contact support for assistance with changing your date of birth.
                                        </Text>
                                    </View>
                                </View>
                            </View>
                        )}
                    </View>
                )
            default:return <></>
        }
    }

    return (
            <View style={{ flex:1 }}>
                <View style={{ flex:1, justifyContent:'center'}}>
                    <FlatList
                        data={sections}
                        key={'dob_sections'}
                        keyExtractor={item => item}
                        renderItem={renderSections}
                    />
                </View>
                {walkthrough ?
                <View nativeID='action_row' style={{ flexDirection:'row', justifyContent:'space-between', padding:10 }}>
                    <Button
                        title='EXIT SETUP'
                        type='text'
                        onPress={() => onClose()}
                    />
                    <Button 
                        style={dob && !error && !loading ? button_styles.wizard_valid : button_styles.wizard_invalid}
                        disabled={error || loading || !dob ? true : false}
                        onPress={() => handleNext()}
                        >
                        <Icons.ChevronIcon size={20} direction='right' color={error ? C.text.error : C.text.success}/>
                    </Button>
                </View>
                :
                <View nativeID='action_row' style={{ flexDirection:'row', justifyContent:'space-between', padding:10 }}>
                    <Button
                        style={{ flex:1, marginRight:5 }}
                        title='BACK'
                        type='close'
                        onPress={() => onClose()}
                    />
                    <Button
                        style={{ flex:3, opacity:update_allowed?1:0.5 }}
                        disabled={!update_allowed}
                        loading={loading}
                        title='UPDATE'
                        type='success'
                        onPress={async() => {
                            await handleUpdatePlayerDOB()
                        }}
                    />
                </View>
                }
        </View>
    )
}

export default DOBManager